import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  ContentCopy,
  Share,
  PictureAsPdf,
  Payment,
  CalendarMonth,
  WhatsApp,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PageHeader } from '../../../shared-components/ui';
import {
  collectInterest,
  CustomerInterestOutstandingReport as ReportType,
  DebitInterestBreakdownRow,
  getInterestOutstandingReport,
  previewInterestCollection,
  InterestCollectionPreview,
} from '../../../services/request/DynamicInterest';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { formatAmountForPdf, formatDateForPdf } from '../../../utils/pdfFormat';
import InterestCollectionConfirmDialog from './InterestCollectionConfirmDialog';
import PaymentDayChangeDialog from './PaymentDayChangeDialog';

const CustomerInterestOutstandingReport: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<ReportType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareLang, setShareLang] = useState<'en' | 'ta'>('en');
  const [sharePreviewOpen, setSharePreviewOpen] = useState(false);

  const [collectDebitId, setCollectDebitId] = useState<string | null>(null);
  const [collectPreview, setCollectPreview] = useState<InterestCollectionPreview | null>(null);
  const [collectLoading, setCollectLoading] = useState(false);
  const [collectError, setCollectError] = useState<string | null>(null);
  const [receivedBy, setReceivedBy] = useState('');

  const [paymentDayDebit, setPaymentDayDebit] = useState<DebitInterestBreakdownRow | null>(null);

  const loadReport = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getInterestOutstandingReport(customerId);
      setReport(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load interest report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const shareText = () => {
    if (!report) return '';
    return shareLang === 'ta' ? report.shareTextTamil || report.shareTextEnglish || '' : report.shareTextEnglish || '';
  };

  const handleCopyShare = async () => {
    const text = shareText();
    if (!text) {
      toast.error('Nothing to copy');
      return;
    }
    await navigator.clipboard.writeText(text);
    toast.success('Report copied to clipboard');
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(shareText());
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleNativeShare = async () => {
    const text = shareText();
    if (navigator.share) {
      await navigator.share({
        title: `Interest report — ${report?.borrowerName}`,
        text,
      });
    } else {
      await handleCopyShare();
    }
  };

  const handleDownloadPdf = () => {
    if (!report) return;

    const marginX = 14;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(`Interest Outstanding Report - ${report.borrowerName}`, marginX, 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const summaryLines = [
      `As of: ${formatDateForPdf(report.asOfDate)}`,
      `Total outstanding: ${formatAmountForPdf(report.totalOutstanding)}`,
      `Monthly interest (expected): ${formatAmountForPdf(report.totalExpectedMonthlyInterest)}`,
      `Pending interest till today: ${formatAmountForPdf(report.totalPendingInterestTillToday)}`,
      `Nearest maturity: ${formatDateForPdf(report.nearestMaturityDate)}`,
      `Rates: ${report.activeInterestRatesLabel}`,
    ];
    let summaryY = 26;
    const lineHeight = 6;
    summaryLines.forEach((line) => {
      doc.text(line, marginX, summaryY);
      summaryY += lineHeight;
    });

    const body = report.debits.map((d) => [
      d.debitId.substring(0, 8),
      formatAmountForPdf(d.outstandingAmount),
      formatAmountForPdf(d.monthlyInterest),
      String(d.paymentDay),
      String(d.elapsedDays),
      formatAmountForPdf(d.perDayInterest),
      formatAmountForPdf(d.interestTillToday),
      d.status || '-',
    ]);

    autoTable(doc, {
      head: [
        [
          'Debit ID',
          'Outstanding',
          'Monthly int.',
          'Pay day',
          'Days',
          'Per day',
          'Till today',
          'Status',
        ],
      ],
      body,
      startY: summaryY + 8,
      margin: { left: marginX, right: marginX },
      tableWidth: pageW - 2 * marginX,
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 2,
        valign: 'middle',
        overflow: 'linebreak',
      },
      headStyles: {
        font: 'helvetica',
        fillColor: [25, 118, 210],
        textColor: 255,
        halign: 'center',
        valign: 'middle',
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'center' },
        4: { halign: 'center' },
        5: { halign: 'right' },
        6: { halign: 'right' },
        7: { halign: 'center' },
      },
      alternateRowStyles: { fillColor: [248, 248, 248] },
    });

    doc.save(`interest_report_${report.borrowerName.replace(/\s+/g, '_')}.pdf`);
  };

  const openCollectDialog = async (debitId: string) => {
    setCollectDebitId(debitId);
    setCollectPreview(null);
    setCollectError(null);
    setReceivedBy('');
    setCollectLoading(true);
    try {
      const preview = await previewInterestCollection(debitId);
      setCollectPreview(preview);
    } catch (e: any) {
      setCollectError(e?.message || 'Failed to load calculation');
    } finally {
      setCollectLoading(false);
    }
  };

  const closeCollectDialog = () => {
    setCollectDebitId(null);
    setCollectPreview(null);
    setCollectError(null);
  };

  const handleConfirmCollect = async (amount: number) => {
    if (!collectDebitId || !collectPreview) return;
    setCollectLoading(true);
    setCollectError(null);
    const calculated = collectPreview.collectibleAmount;
    const overridden = amount !== calculated;
    try {
      await collectInterest(collectDebitId, {
        amount,
        paymentReceivedBy: receivedBy || undefined,
        note: overridden
          ? `Collected via dynamic interest module — ${collectPreview.elapsedDays} days (adjusted from calculated ${calculated})`
          : `Collected via dynamic interest module — ${collectPreview.elapsedDays} days`,
      });
      toast.success(`Interest ${formatCurrency(amount)} collected`);
      closeCollectDialog();
      await loadReport();
    } catch (e: any) {
      setCollectError(e?.response?.data?.message || e?.message || 'Collection failed');
    } finally {
      setCollectLoading(false);
    }
  };

  const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <ToastContainer position="top-right" autoClose={3000} />
      <PageHeader
        eyebrow="Lending · Ops"
        title="Interest & outstanding report"
        subtitle={report ? `${report.borrowerName} · As of ${formatDate(report.asOfDate)}` : 'Dynamic debit breakdown and pending interest.'}
        actions={
          <>
            <IconButton onClick={() => navigate(`/admin/customers/${customerId}/debits`)} color="primary" aria-label="Back">
              <ArrowBack />
            </IconButton>
            <Button startIcon={<Refresh />} onClick={loadReport} disabled={loading}>
              Refresh
            </Button>
            <Button startIcon={<ContentCopy />} variant="outlined" onClick={() => setSharePreviewOpen(true)} disabled={!report}>
              Copy & share
            </Button>
            <Button startIcon={<PictureAsPdf />} variant="outlined" onClick={handleDownloadPdf} disabled={!report}>
              PDF
            </Button>
          </>
        }
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {report && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard label="Total debit amount" value={formatCurrency(report.totalDebitAmount)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard label="Current outstanding" value={formatCurrency(report.totalOutstanding)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard label="Expected monthly interest" value={formatCurrency(report.totalExpectedMonthlyInterest)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard label="Pending interest till today" value={formatCurrency(report.totalPendingInterestTillToday)} highlight />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard label="Nearest maturity" value={formatDate(report.nearestMaturityDate)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard label="Active interest rates" value={report.activeInterestRatesLabel} />
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Debit-wise breakdown
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Formula: (Monthly Interest ÷ 30) × Elapsed Days · Days counted from current month pay day (or last collection this cycle)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Debit ID</TableCell>
                      <TableCell align="right">Principal</TableCell>
                      <TableCell align="right">Outstanding</TableCell>
                      <TableCell align="right">Monthly int.</TableCell>
                      <TableCell align="center">Rate %</TableCell>
                      <TableCell align="center">Pay day</TableCell>
                      <TableCell>Maturity</TableCell>
                      <TableCell>Last collected</TableCell>
                      <TableCell align="center">Days</TableCell>
                      <TableCell align="right">Per day</TableCell>
                      <TableCell align="right">Till today</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.debits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} align="center">
                          No active INTEREST debits with outstanding balance
                        </TableCell>
                      </TableRow>
                    ) : (
                      report.debits.map((row) => (
                        <TableRow key={row.debitId} hover>
                          <TableCell>{row.debitId.substring(0, 8)}…</TableCell>
                          <TableCell align="right">{formatCurrency(row.principalAmount)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.outstandingAmount)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.monthlyInterest)}</TableCell>
                          <TableCell align="center">{row.interestRatePercent}%</TableCell>
                          <TableCell align="center">{row.paymentDay}</TableCell>
                          <TableCell>{formatDate(row.maturityDate)}</TableCell>
                          <TableCell>{formatDate(row.lastInterestCollectedDate)}</TableCell>
                          <TableCell align="center">{row.elapsedDays}</TableCell>
                          <TableCell align="right">{formatCurrency(row.perDayInterest)}</TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600} color="primary.main">
                              {formatCurrency(row.interestTillToday)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={row.status} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Collect interest">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openCollectDialog(row.debitId)}
                                disabled={row.interestTillToday <= 0}
                              >
                                <Payment fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Update payment day">
                              <IconButton size="small" onClick={() => setPaymentDayDebit(row)}>
                                <CalendarMonth fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      <InterestCollectionConfirmDialog
        open={!!collectDebitId}
        loading={collectLoading}
        preview={collectPreview}
        error={collectError}
        onClose={closeCollectDialog}
        onConfirm={handleConfirmCollect}
        paymentReceivedBy={receivedBy}
        onPaymentReceivedByChange={setReceivedBy}
      />

      <PaymentDayChangeDialog
        open={!!paymentDayDebit}
        debitId={paymentDayDebit?.debitId ?? null}
        existingPaymentDay={paymentDayDebit?.paymentDay ?? 1}
        onClose={() => setPaymentDayDebit(null)}
        onUpdated={loadReport}
      />

      <Dialog open={sharePreviewOpen} onClose={() => setSharePreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Customer share message</DialogTitle>
        <DialogContent>
          <ToggleButtonGroup
            value={shareLang}
            exclusive
            onChange={(_, v) => v && setShareLang(v)}
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="en">English</ToggleButton>
            <ToggleButton value="ta">தமிழ் (Tamil)</ToggleButton>
          </ToggleButtonGroup>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13 }}>
            {shareText() || 'No share text available'}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<WhatsApp />} onClick={handleWhatsAppShare} color="success">
            WhatsApp
          </Button>
          <Button startIcon={<Share />} onClick={handleNativeShare}>
            Share
          </Button>
          <Button startIcon={<ContentCopy />} variant="contained" onClick={handleCopyShare}>
            Copy
          </Button>
          <Button onClick={() => setSharePreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function SummaryCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <Card variant="outlined" sx={highlight ? { borderColor: 'primary.main', borderWidth: 2 } : undefined}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" color={highlight ? 'primary' : 'text.primary'}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default CustomerInterestOutstandingReport;
